/**
 * Distance service using Geoapify for geocoding and routing.
 * Used for "Containervervoer plaats van bestemming" (Seafreight FCL).
 */

import {
  canSpendCredits,
  recordCreditsUsed,
  GEOCODING_CREDITS,
  estimateRoutingCredits,
} from "../utils/geoapifyCreditTracker";
import {
  getCachedGeocode,
  setCachedGeocode,
  getCachedRouting,
  setCachedRouting,
} from "../utils/geoapifyCache";

const GEOAPIFY_BASE = "https://api.geoapify.com/v1";
const apiKey = import.meta.env.VITE_GEOAPIFY_API_KEY;

export interface GeocodeResult {
  lat: number;
  lon: number;
}

export interface AddressInput {
  street?: string;
  housenumber?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  /** Free-form address (alternative to structured fields) */
  text?: string;
}

/**
 * Geocode an address to lat/lon using Geoapify Geocoding API.
 */
export async function geocodeAddress(
  address: AddressInput,
): Promise<GeocodeResult | null> {
  if (!apiKey || apiKey === "your_geoapify_api_key_here") {
    console.warn("VITE_GEOAPIFY_API_KEY not configured");
    return null;
  }
  const cached = getCachedGeocode(address);
  if (cached) return cached;

  if (!canSpendCredits(GEOCODING_CREDITS)) {
    console.warn("Geoapify daily credit limit reached (geocoding)");
    return null;
  }

  const params = new URLSearchParams();
  params.set("apiKey", apiKey);
  params.set("format", "json");
  params.set("limit", "1");

  if (address.text) {
    params.set("text", address.text);
  } else {
    const parts: string[] = [];
    if (address.housenumber) parts.push(address.housenumber);
    if (address.street) parts.push(address.street);
    if (address.postal_code) parts.push(address.postal_code);
    if (address.city) parts.push(address.city);
    if (address.country) parts.push(address.country);
    if (parts.length === 0) return null;
    params.set("text", parts.join(", "));
  }

  try {
    const res = await fetch(
      `${GEOAPIFY_BASE}/geocode/search?${params.toString()}`,
    );
    const data = await res.json();
    const results = data?.results;
    if (!results?.length) return null;
    const first = results[0];
    const lat = first?.lat;
    const lon = first?.lon;
    if (typeof lat !== "number" || typeof lon !== "number") return null;
    recordCreditsUsed(GEOCODING_CREDITS);
    const result = { lat, lon };
    setCachedGeocode(address, result);
    return result;
  } catch (err) {
    console.error("Geoapify geocoding error:", err);
    return null;
  }
}

export interface RoadDistanceResult {
  distanceKm: number;
  routeGeometry?: number[][];
}

/**
 * Get road distance in km and route geometry between two coordinates using Geoapify Routing API.
 * Uses truck mode for container transport.
 * routeGeometry = array of [lon, lat] for polyline.
 */
export async function getRoadDistanceKm(
  origin: { lat: number; lon: number },
  dest: { lat: number; lon: number },
): Promise<RoadDistanceResult | null> {
  if (!apiKey || apiKey === "your_geoapify_api_key_here") {
    console.warn("VITE_GEOAPIFY_API_KEY not configured");
    return null;
  }
  const cached = getCachedRouting(origin, dest);
  if (cached) return cached;

  // Routing: 1 base + extra for long routes; check conservatively (assume up to 10 credits)
  if (!canSpendCredits(10)) {
    console.warn("Geoapify daily credit limit reached (routing)");
    return null;
  }

  const waypoints = `${origin.lat},${origin.lon}|${dest.lat},${dest.lon}`;
  const params = new URLSearchParams({
    waypoints,
    mode: "truck",
    format: "geojson",
    units: "metric",
    apiKey,
  });

  try {
    const res = await fetch(`${GEOAPIFY_BASE}/routing?${params.toString()}`);
    const data = await res.json();

    if (data?.statusCode >= 400) {
      console.error("Geoapify routing error:", data);
      return null;
    }

    const feature = data?.features?.[0];
    const props = feature?.properties;
    const distance = props?.distance;
    const units = props?.distance_units;

    if (typeof distance !== "number") return null;

    const distanceKm = units === "m" || units === "meters" ? distance / 1000 : distance;
    recordCreditsUsed(estimateRoutingCredits(distanceKm));

    let routeGeometry: number[][] | undefined;
    const geom = feature?.geometry;
    if (geom?.coordinates) {
      if (geom.type === "LineString") {
        routeGeometry = geom.coordinates as number[][];
      } else if (geom.type === "MultiLineString") {
        routeGeometry = (geom.coordinates as number[][][]).flat();
      }
    }

    const result: RoadDistanceResult = { distanceKm, routeGeometry: routeGeometry ?? [] };
    setCachedRouting(origin, dest, result);
    return result;
  } catch (err) {
    console.error("Geoapify routing error:", err);
    return null;
  }
}
